/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, jsx } from '@compiled/react';
import { useAtomValue } from 'jotai';
import { loadable } from 'jotai/utils';
import { type MessageDescriptor, FormattedMessage, useIntl } from 'react-intl';

import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { GlobalMetricsAtom } from '../../../state';

import { messages } from './messages';
import { selectStatValues } from './utils';

// Wrap the suspending atom with `loadable` so this component can render its
// own per-card spinner placeholders synchronously while the metrics fetch is
// still in flight, instead of suspending the entire `OverviewSection`.
const GlobalMetricsLoadable = loadable(GlobalMetricsAtom);

type StatKey = 'entitiesIndexed' | 'relationshipsCreated' | 'queriesThisWeek' | 'customersUsing';

const STAT_LABELS: { key: StatKey; label: MessageDescriptor }[] = [
	{ key: 'entitiesIndexed', label: messages.entitiesIndexedLabel },
	{ key: 'relationshipsCreated', label: messages.relationshipsCreatedLabel },
	{ key: 'queriesThisWeek', label: messages.queriesThisWeekLabel },
	{ key: 'customersUsing', label: messages.customersUsingLabel },
];

const styles = cssMap({
	statsRow: {
		display: 'grid',
		gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		minHeight: '240px',
		'@media (max-width: 62.5rem)': {
			gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
		},
		'@media (max-width: 37.5rem)': {
			gridTemplateColumns: '1fr',
		},
	},
	statCard: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		padding: '30px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		gap: '20px',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderRight: '2px dotted #505258',
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		borderBottom: '2px dotted #505258',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:last-of-type': {
			borderRight: 'none',
		},
	},
	statLabel: {
		font: token('font.body'),
		margin: 0,
		// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
		color: '#8C8F97',
		fontFamily: token('font.family.code'),
		fontWeight: token('font.weight.medium'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		textTransform: 'uppercase',
		whiteSpace: 'pre-line',
	},
	statValue: {
		margin: 0,
		color: token('color.text'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		fontSize: '27px',
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '32px',
		fontVariantNumeric: 'tabular-nums lining-nums',
	},
});

export function Stats(): JSX.Element {
	const { locale } = useIntl();
	const loadableState = useAtomValue(GlobalMetricsLoadable);

	const isLoading = loadableState.state === 'loading';
	// `loadable` exposes the resolved atom value on `.data` once `state` is
	// `'hasData'`. The atom itself never throws and always resolves to
	// `{ data, error }`, so the `'hasError'` branch should never fire — but
	// we still defend against it by treating it as a missing payload, which
	// makes `selectStatValues` fall back to the shared `'N/A'` placeholder.
	const metrics = loadableState.state === 'hasData' ? loadableState.data.data : null;
	const error = loadableState.state === 'hasData' ? loadableState.data.error : null;

	if (error) {
		// eslint-disable-next-line no-console
		console.warn('Stats: falling back to placeholder stats —', error);
	}

	const statValues = selectStatValues(metrics, locale);

	return (
		<div css={styles.statsRow}>
			{STAT_LABELS.map((stat) => (
				<div key={stat.label.id} css={styles.statCard}>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p css={styles.statLabel}>
						<FormattedMessage {...stat.label} />
					</p>
					{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
					<p css={styles.statValue}>
						{
							// TODO: GRAPHYTE-2509 Error fallback / Suspense Fallback
							isLoading ? <Spinner size="medium" appearance="inherit" /> : statValues[stat.key]
						}
					</p>
				</div>
			))}
		</div>
	);
}
